/**
 * Section Navigation block — smooth scroll + scrollspy.
 *
 * The block reuses the microsite-nav markup (so the cascade / mobile
 * drill behaviour is wired by microsite-nav.js). This module adds the
 * in-page behaviour the microsite nav doesn't have, scoped to the
 * block's `[data-section-nav]` wrapper so the real microsite navs are
 * untouched:
 *
 *   - Hash links (href="#id") smooth-scroll to their target, offset by
 *     any sticky header, and close the mobile overlay if open.
 *   - A scrollspy highlights the matching nav item(s) as each target
 *     section enters the viewport (IntersectionObserver).
 */

// Offset for the fixed site header. Reads the theme's --whf-header-height
// custom property (kept current by navigation.js, and which already
// accounts for the alert ticker) rather than re-measuring the header, so
// the section title clears the header instead of hiding behind it. Adds
// 1rem of breathing room to match the theme's scroll-margin convention.
const getHeaderOffset = () => {
	const raw = window
		.getComputedStyle( document.documentElement )
		.getPropertyValue( '--whf-header-height' );
	const height = parseInt( raw, 10 );
	return ( Number.isNaN( height ) ? 60 : height ) + 16;
};

const smoothScrollTo = ( target ) => {
	const top =
		target.getBoundingClientRect().top +
		window.pageYOffset -
		getHeaderOffset();
	window.scrollTo( { top: Math.max( 0, top ), behavior: 'smooth' } );
};

// Pull the same-page fragment id from a link, or null if it isn't one.
const fragmentId = ( link ) => {
	const href = link.getAttribute( 'href' ) || '';
	if ( href.startsWith( '#' ) ) {
		return href.slice( 1 );
	}
	// Same-path links that carry a fragment (e.g. /page/#id).
	if ( href.includes( '#' ) ) {
		try {
			const url = new URL( link.href, window.location.href );
			if ( url.pathname === window.location.pathname && url.hash ) {
				return url.hash.slice( 1 );
			}
		} catch ( e ) {
			return null;
		}
	}
	return null;
};

const setupSectionNav = ( nav ) => {
	const links = nav.querySelectorAll(
		'.whf-microsite-nav__link, .whf-microsite-nav__sublink, .whf-microsite-nav__mobile-link'
	);

	links.forEach( ( link ) => {
		const id = fragmentId( link );
		if ( ! id ) {
			return;
		}
		const target = document.getElementById( id );

		link.addEventListener( 'click', ( e ) => {
			if ( ! target ) {
				return; // No matching section — let it behave like a normal link.
			}
			e.preventDefault();

			// Close the mobile overlay if the click came from inside it.
			const overlay = nav.querySelector( '[data-microsite-nav-mobile]' );
			if ( overlay && overlay.classList.contains( 'is-open' ) ) {
				const closeBtn = nav.querySelector( '[data-microsite-nav-mobile-close]' );
				if ( closeBtn ) {
					closeBtn.click();
				}
			}

			smoothScrollTo( target );
			if ( window.history && window.history.pushState ) {
				window.history.pushState( null, '', '#' + id );
			}
		} );
	} );

	// Active-section highlighting (the scrollspy) is handled by microsite-nav.js,
	// which the reused microsite markup already wires up. It groups the bar,
	// cascade and mobile links by hash and toggles them together. We deliberately
	// do NOT add a second observer here — two scrollspies with different bands
	// fought over the same `is-active` class and produced erratic highlighting.
};

export const initSectionNav = () => {
	document.querySelectorAll( '[data-section-nav]' ).forEach( setupSectionNav );
};
